// ============================================
// Path: /frontend/src/pages/EditListingPage.jsx
// New page for editing existing listings
// ============================================

import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import { useToast } from '../hooks/useToast';
import { uploadAPI } from '../api/upload.api';
import { dashboardAPI } from '../api/dashboard.api';
import { counties } from '../lib/locations';
import { makes } from '../lib/vehicles';
import { vehicleTypes, propertyTypes } from '../lib/categories';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import ListingImage from '../components/ListingImage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Upload, Lightbulb, Loader2, X, ArrowLeft } from 'lucide-react';

export default function EditListingPage() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [county, setCounty] = useState('');
  const [subCounty, setSubCounty] = useState('');
  const [category, setCategory] = useState('');
  const [type, setType] = useState('');
  const [subType, setSubType] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState('');
  const fileInputRef = useRef(null);

  const [tags, setTags] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuggesting, setIsSuggesting] = useState(false);

  const API_BASE_URL = import.meta.env.VITE_BASE_URL || 'http://localhost:5000';

  // Fetch listing data
  useEffect(() => {
    const fetchListing = async () => {
      if (!id) return;

      try {
        console.log('[EditListingPage] Fetching listing:', id);
        const { listing } = await dashboardAPI.getListingById(id);
        
        console.log('[EditListingPage] Listing data:', listing);

        // Populate form fields
        setTitle(listing.title || '');
        setDescription(listing.description || '');
        setPrice(listing.price?.toString() || '');
        setCounty(listing.county || '');
        setSubCounty(listing.subCounty || '');
        setCategory(listing.category?.toLowerCase() || '');
        setType(listing.type || '');
        setSubType(listing.subType || '');
        setMake(listing.make || '');
        setModel(listing.model || '');
        setTags(listing.tags || []);

        // Handle image
        // Handle image
        if (listing.imageUrl) {
          let imageUrl = listing.imageUrl;
          
          // Handle array format (new schema)
          if (Array.isArray(imageUrl) && imageUrl.length > 0) {
            imageUrl = imageUrl[0];
          }
          
          // Build full URL if it's a string
          if (imageUrl && typeof imageUrl === 'string') {
            const fullImageUrl = imageUrl.startsWith('http')
              ? imageUrl
              : `${API_BASE_URL}${imageUrl}`;
            setExistingImageUrl(fullImageUrl);
            setImagePreview(fullImageUrl);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('[EditListingPage] Error fetching listing:', error);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Failed to load listing data',
        });
        navigate('/dashboard');
      }
    };

    if (user && !authLoading) {
      fetchListing();
    }
  }, [id, user, authLoading, navigate, toast]);

  const handleCountyChange = (value) => {
    setCounty(value);
    setSubCounty('');
  };

  const handleMakeChange = (value) => {
    setMake(value);
    setModel('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      console.log('[EditListingPage] New image selected:', file.name);
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageRemove = () => {
    setImageFile(null);
    setImagePreview(existingImageUrl || null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAISuggestions = async () => {
    if (!description) {
      toast({
        variant: 'destructive',
        title: 'Description needed',
        description: 'Please write a description before getting AI suggestions.',
      });
      return;
    }

    setIsSuggesting(true);

    try {
      const words = description.toLowerCase().split(/\s+/);
      const commonWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by'];
      const newTags = words
        .filter(word => word.length > 4 && !commonWords.includes(word))
        .slice(0, 5)
        .filter(tag => !tags.includes(tag));

      if (newTags.length > 0) {
        setTags([...tags, ...newTags]);
        toast({
          title: 'Tags Added!',
          description: `${newTags.length} tags have been added.`,
        });
      } else {
        toast({
          title: 'No New Tags',
          description: 'Could not generate additional tags.',
        });
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Could not generate tag suggestions.',
      });
    } finally {
      setIsSuggesting(false);
    }
  };

  const handleTagInput = (e) => {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault();
      const newTag = e.target.value.trim();
      if (newTag && !tags.includes(newTag)) {
        setTags([...tags, newTag]);
      }
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('[EditListingPage] Form submitted');

    setIsSubmitting(true);

    try {
      let imageUrl = existingImageUrl;

      // Upload new image if selected
      if (imageFile) {
        console.log('[EditListingPage] Uploading new image...');
        const uploadResponse = await uploadAPI.uploadImage(imageFile);
        imageUrl = uploadResponse.data?.url || uploadResponse.url;
        console.log('[EditListingPage] New image uploaded:', imageUrl);
      }

      const listingData = {
        title,
        description,
        price: Number(price),
        county,
        subCounty,
        category: category.toUpperCase(),
        type,
        tags,
        imageUrl,
        subType: subType || null,
      };

      if (category === 'vehicle') {
        listingData.make = make;
        listingData.model = model;
      }

      console.log('[EditListingPage] Updating listing:', listingData);
      await dashboardAPI.updateListing(id, listingData);

      toast({
        title: 'Listing Updated!',
        description: 'Your listing has been updated and is now pending admin re-approval.',
        duration: 7000,
      });

      navigate('/dashboard');
    } catch (error) {
      console.error('[EditListingPage] Update error:', error);
      toast({
        variant: 'destructive',
        title: 'Update Failed',
        description: error.response?.data?.message || 'Could not update listing',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading || !user || loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }

  const subCounties = counties.find((c) => c.name === county)?.subCounties || [];
  const models = makes.find((m) => m.name === make)?.models || [];
  const propertySubTypes = propertyTypes.find((p) => p.name === type)?.subTypes || [];
  const showPropertySubTypes = category === 'property' && propertySubTypes.length > 0;

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-12 md:py-20">
        <Card className="max-w-3xl mx-auto">
          <CardHeader>
            <div className="flex items-center gap-4 mb-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <CardTitle className="text-3xl font-bold">
                  Edit Listing
                </CardTitle>
                <CardDescription>
                  Update your listing details below
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Image Upload */}
              <div className="space-y-2">
                <Label>Listing Image</Label>
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  ref={fileInputRef}
                />
                {imagePreview ? (
                  <div className="relative group">
                    <ListingImage
                      src={imagePreview}
                      alt="Listing preview"
                      type={category}
                      className="rounded-md object-cover aspect-video w-full"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={handleImageRemove}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      className="absolute bottom-2 right-2"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Change Image
                    </Button>
                  </div>
                ) : (
                  <div
                    className="w-full aspect-video border-2 border-dashed border-muted-foreground/30 rounded-md flex flex-col items-center justify-center cursor-pointer hover:bg-muted/50"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">Click to upload an image</p>
                  </div>
                )}
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Listing Title</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., 2023 Toyota Prado TX"
                  required
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Provide a detailed description..."
                  required
                />
              </div>

              {/* Category and Price */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    onValueChange={(value) => {
                      setCategory(value);
                      setType('');
                      setSubType('');
                    }}
                    value={category}
                    required
                  >
                    <SelectTrigger id="category">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vehicle">Vehicle</SelectItem>
                      <SelectItem value="property">Property</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">Price (KES)</Label>
                  <Input
                    id="price"
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="e.g., 5200000"
                    required
                  />
                </div>
              </div>

              {/* County and Sub-County */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="county">County</Label>
                  <Select onValueChange={handleCountyChange} value={county} required>
                    <SelectTrigger id="county">
                      <SelectValue placeholder="Select a county" />
                    </SelectTrigger>
                    <SelectContent>
                      {counties.map((c) => (
                        <SelectItem key={c.name} value={c.name}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subCounty">Sub-County</Label>
                  <Select
                    onValueChange={setSubCounty}
                    value={subCounty}
                    required
                    disabled={!county}
                  >
                    <SelectTrigger id="subCounty">
                      <SelectValue placeholder="Select a sub-county" />
                    </SelectTrigger>
                    <SelectContent>
                      {subCounties.map((sc) => (
                        <SelectItem key={sc} value={sc}>
                          {sc}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Vehicle Make and Model */}
              {category === 'vehicle' && (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="make">Make</Label>
                    <Select onValueChange={handleMakeChange} value={make} required>
                      <SelectTrigger id="make">
                        <SelectValue placeholder="Select a make" />
                      </SelectTrigger>
                      <SelectContent>
                        {makes.map((m) => (
                          <SelectItem key={m.name} value={m.name}>
                            {m.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="model">Model</Label>
                    <Select
                      onValueChange={setModel}
                      value={model}
                      required
                      disabled={!make}
                    >
                      <SelectTrigger id="model">
                        <SelectValue placeholder="Select a model" />
                      </SelectTrigger>
                      <SelectContent>
                        {models.map((m) => (
                          <SelectItem key={m} value={m}>
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Type and Sub-Type */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="type">Type</Label>
                  <Select
                    onValueChange={setType}
                    value={type}
                    required
                    disabled={!category}
                  >
                    <SelectTrigger id="type">
                      <SelectValue placeholder="Select a type" />
                    </SelectTrigger>
                    <SelectContent>
                      {category === 'vehicle' &&
                        vehicleTypes.map((item) => (
                          <SelectItem key={item} value={item}>
                            {item}
                          </SelectItem>
                        ))}
                      {category === 'property' &&
                        propertyTypes.map((item) => (
                          <SelectItem key={item.name} value={item.name}>
                            {item.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {showPropertySubTypes && (
                  <div className="space-y-2">
                    <Label htmlFor="subType">Sub-Type</Label>
                    <Select onValueChange={setSubType} value={subType} required>
                      <SelectTrigger id="subType">
                        <SelectValue placeholder="Select a sub-type" />
                      </SelectTrigger>
                      <SelectContent>
                        {propertySubTypes.map((st) => (
                          <SelectItem key={st} value={st}>
                            {st}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Tags */}
              <div className="space-y-4">
                <Label>Tags</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isSuggesting}
                    onClick={handleAISuggestions}
                  >
                    {isSuggesting ? (
                      <Loader2 className="mr-2 animate-spin" />
                    ) : (
                      <Lightbulb className="mr-2" />
                    )}
                    Get AI Suggestions
                  </Button>
                </div>
                <Input
                  placeholder="Add your own tags, comma-separated"
                  onKeyDown={handleTagInput}
                />
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <Badge key={tag} variant="secondary">
                        {tag}
                        <button
                          type="button"
                          onClick={() => setTags(tags.filter(t => t !== tag))}
                          className="ml-1 hover:text-destructive"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-4 !mt-8">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  className="flex-1"
                  onClick={() => navigate('/dashboard')}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="lg"
                  className="flex-1 font-bold"
                  disabled={isSubmitting}
                >
                  {isSubmitting && <Loader2 className="mr-2 animate-spin" />}
                  Update Listing
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}